/**
 * Admin: Role Management page.
 *
 * List all roles, create custom roles, edit permissions,
 * and delete custom roles. System roles are read-only.
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { apiClient } from '@/services/apiClient'
import {
  Button,
  Input,
  Badge,
  Alert,
  AlertDescription,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Textarea,
  Spinner,
  Tip,
} from '@/components/ui'
import {
  Plus,
  Trash2,
  Lock,
  Pencil,
  Eye,
  ChevronDown,
  ChevronRight,
  Users,
} from 'lucide-react'
import {
  groupCounts,
  isAvailable,
  permissionGroups,
  withGroupToggled,
  type PermissionGroup,
} from './roles/permissionGroups'
import { NOT_ON_DEPLOYMENT, UNAVAILABLE_RULE, gateTip, heldText } from './roles/rolesText'

// ── Types ──

interface PermissionInfo {
  id: string
  key: string
  resource: string
  action: string
  display_name: string
  description: string | null
  feature_gate: string | null
  /** SA-1126: whether `feature_gate` is on in this deployment (the server's answer). */
  feature_enabled?: boolean
}

interface RoleSummary {
  id: string
  name: string
  display_name: string
  description: string | null
  is_system: boolean
  is_default: boolean
  user_count: number
  permission_count: number
}

interface RoleDetail {
  id: string
  name: string
  display_name: string
  description: string | null
  is_system: boolean
  is_default: boolean
  parent_role_name: string | null
  permissions: PermissionInfo[]
  user_count: number
  created_at: string | null
  updated_at: string | null
}

interface PermissionManifest {
  permissions: PermissionInfo[]
  resources: string[]
}

// ── Permission Picker component ──

/**
 * SA-1126: a permission whose module is off on this deployment
 * (`feature_enabled: false`, the server's answer) is disabled; a group of
 * nothing but such rows starts collapsed, after the "Unavailable on this
 * deployment" rule. A held one stays checked and rides the save unchanged.
 */
function PermissionPicker({
  manifest,
  selectedKeys,
  onChange,
  readOnly = false,
}: {
  manifest: PermissionManifest
  selectedKeys: Set<string>
  onChange: (keys: Set<string>) => void
  readOnly?: boolean
}) {
  const groups = permissionGroups(manifest.resources, manifest.permissions)
  const [expandedResources, setExpandedResources] = useState<Set<string>>(
    () => new Set(groups.open.map((g) => g.resource))
  )

  const toggleResource = (resource: string) => {
    setExpandedResources((prev) => {
      const next = new Set(prev)
      if (next.has(resource)) {
        next.delete(resource)
      } else {
        next.add(resource)
      }
      return next
    })
  }

  const togglePermission = (perm: PermissionInfo) => {
    if (readOnly || !isAvailable(perm)) return
    const next = new Set(selectedKeys)
    if (next.has(perm.key)) {
      next.delete(perm.key)
    } else {
      next.add(perm.key)
    }
    onChange(next)
  }

  const toggleAllInResource = (group: PermissionGroup<PermissionInfo>) => {
    if (readOnly) return
    onChange(withGroupToggled(group, selectedKeys))
  }

  const renderGroup = (group: PermissionGroup<PermissionInfo>) => {
    const { resource, permissions: perms, available } = group
    const isExpanded = expandedResources.has(resource)
    const counts = groupCounts(group, selectedKeys)
    const allUnavailable = perms.length > 0 && available.length === 0

    return (
      <div
        key={resource}
        className="border border-border rounded-md overflow-hidden"
      >
        {/* Resource header */}
        <button
          type="button"
          onClick={() => toggleResource(resource)}
          className="flex items-center justify-between w-full px-3 py-2 bg-muted hover:bg-muted-foreground/10 transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            <span
              className={`text-sm font-medium uppercase tracking-wider ${
                allUnavailable ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              {resource}
            </span>
          </div>
          {allUnavailable ? (
            counts.held > 0 && (
              <Badge variant="neutral" className="text-xs">
                {heldText(counts.held)}
              </Badge>
            )
          ) : (
            <Badge variant={counts.selected > 0 ? 'default' : 'secondary'} className="text-xs">
              {counts.selected}/{counts.total}
            </Badge>
          )}
        </button>

        {/* Permissions list */}
        {isExpanded && (
          <div className="px-3 py-2 space-y-1">
            {/* Select all: the rows a role could be given */}
            {!readOnly && available.length > 1 && (
              <label className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                <input
                  type="checkbox"
                  checked={counts.selected === counts.total}
                  onChange={() => toggleAllInResource(group)}
                  className="rounded-sm text-primary focus:ring-ring"
                />
                Select all
              </label>
            )}
            {perms.map((perm) => {
              const isSelected = selectedKeys.has(perm.key)
              const permAvailable = isAvailable(perm)
              const locked = readOnly || !permAvailable
              return (
                <label
                  key={perm.key}
                  className={`flex items-start gap-2 px-2 py-1.5 rounded-md transition-colors ${
                    locked ? 'cursor-default' : 'cursor-pointer hover:bg-accent'
                  } ${permAvailable ? '' : 'opacity-60'}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => togglePermission(perm)}
                    disabled={locked}
                    className="mt-0.5 rounded-sm text-primary focus:ring-ring disabled:opacity-60"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground">
                        {perm.display_name}
                      </span>
                      {perm.feature_gate && (
                        <FeatureGateBadge gate={perm.feature_gate} available={permAvailable} />
                      )}
                    </div>
                    {perm.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {perm.description}
                      </p>
                    )}
                  </div>
                </label>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {groups.open.map(renderGroup)}
      {groups.unavailable.length > 0 && (
        <div className="flex items-center gap-2 px-1 pt-3 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span>{UNAVAILABLE_RULE}</span>
          <span aria-hidden="true" className="h-px flex-1 bg-border" />
        </div>
      )}
      {groups.unavailable.map(renderGroup)}
    </div>
  )
}

function FeatureGateBadge({ gate, available }: { gate: string; available: boolean }) {
  if (available) {
    return (
      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
        {gate}
      </Badge>
    )
  }
  return (
    <Tip text={gateTip(gate)}>
      <Badge variant="muted-soft" tabIndex={0} className="text-[10px] px-1.5 py-0">
        {NOT_ON_DEPLOYMENT}
      </Badge>
    </Tip>
  )
}

// ── Main page ──

export function RoleManagementPage() {
  const [roles, setRoles] = useState<RoleSummary[]>([])
  const [manifest, setManifest] = useState<PermissionManifest | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Create role dialog
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: '',
    display_name: '',
    description: '',
  })
  const [createPermissions, setCreatePermissions] = useState<Set<string>>(new Set())
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Edit/view role dialog
  const [editRole, setEditRole] = useState<RoleDetail | null>(null)
  const [editForm, setEditForm] = useState({
    display_name: '',
    description: '',
  })
  const [editPermissions, setEditPermissions] = useState<Set<string>>(new Set())
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<RoleSummary | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchRoles = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/admin/roles')
      if (!res.ok) throw new Error('Failed to fetch roles')
      const data = await res.json()
      setRoles(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const fetchManifest = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/admin/permissions/manifest')
      if (!res.ok) throw new Error('Failed to fetch permissions')
      const data = await res.json()
      setManifest(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions')
    }
  }, [])

  useEffect(() => {
    fetchRoles()
    fetchManifest()
  }, [fetchRoles, fetchManifest])

  // Clear success message after a delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [success])

  // ── Create role ──

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setIsCreating(true)
    setCreateError(null)

    try {
      const res = await apiClient.fetch('/api/admin/roles', {
        method: 'POST',
        body: JSON.stringify({
          name: createForm.name,
          display_name: createForm.display_name,
          description: createForm.description || null,
          permission_keys: Array.from(createPermissions),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed to create role' }))
        throw new Error(data.detail || 'Failed to create role')
      }
      setShowCreate(false)
      setCreateForm({ name: '', display_name: '', description: '' })
      setCreatePermissions(new Set())
      setSuccess('Role created successfully')
      await fetchRoles()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create role')
    } finally {
      setIsCreating(false)
    }
  }

  // ── Open role detail ──

  const openRoleDetail = async (roleSummary: RoleSummary) => {
    setIsLoadingDetail(true)
    try {
      const res = await apiClient.fetch(`/api/admin/roles/${roleSummary.id}`)
      if (!res.ok) throw new Error('Failed to load role details')
      const detail: RoleDetail = await res.json()
      setEditRole(detail)
      setEditForm({
        display_name: detail.display_name,
        description: detail.description || '',
      })
      setEditPermissions(new Set(detail.permissions.map((p) => p.key)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load role')
    } finally {
      setIsLoadingDetail(false)
    }
  }

  // ── Save role changes ──

  const handleSave = async (e: FormEvent) => {
    e.preventDefault()
    if (!editRole || editRole.is_system) return
    setIsSaving(true)

    try {
      const res = await apiClient.fetch(`/api/admin/roles/${editRole.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          display_name: editForm.display_name,
          description: editForm.description || null,
          permission_keys: Array.from(editPermissions),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed to update role' }))
        throw new Error(data.detail || 'Failed to update role')
      }
      setEditRole(null)
      setSuccess('Role updated successfully')
      await fetchRoles()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role')
    } finally {
      setIsSaving(false)
    }
  }

  // ── Delete role ──

  const handleDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      const res = await apiClient.fetch(`/api/admin/roles/${deleteTarget.id}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail || 'Failed to delete role')
      }
      setDeleteTarget(null)
      setSuccess('Role deleted successfully')
      await fetchRoles()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role')
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="Role Management"
        subtitle={`${roles.length} role${roles.length !== 1 ? 's' : ''} configured`}
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Role
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Roles table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Role</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Permissions</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Users</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {roles.map((role) => (
                <tr key={role.id} className="hover:bg-muted">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {role.is_system && (
                        <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      )}
                      <div>
                        <div className="font-medium text-foreground">
                          {role.display_name}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {role.description || role.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={role.is_system ? 'secondary' : 'default'}>
                      {role.is_system ? 'System' : 'Custom'}
                    </Badge>
                    {role.is_default && (
                      <Badge variant="warning" className="ml-1 text-[10px]">
                        Default
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {role.permission_count}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Users className="w-3.5 h-3.5" />
                      {role.user_count}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* View/Edit */}
                      <Button
                        variant="ghost"
                        size="sm"
                        title={role.is_system ? 'View role' : 'Edit role'}
                        onClick={() => openRoleDetail(role)}
                        disabled={isLoadingDetail}
                      >
                        {isLoadingDetail ? (
                          <Spinner size="sm" />
                        ) : role.is_system ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <Pencil className="w-4 h-4" />
                        )}
                      </Button>

                      {/* Delete (custom roles only, with no users) */}
                      {!role.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          title={
                            role.user_count > 0
                              ? 'Unassign all users before deleting'
                              : 'Delete role'
                          }
                          disabled={role.user_count > 0}
                          onClick={() => setDeleteTarget(role)}
                          className="text-destructive hover:text-destructive/90"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create Role Modal ── */}
      <Modal open={showCreate} onOpenChange={setShowCreate}>
        <ModalContent size="lg">
          <form onSubmit={handleCreate}>
            <ModalHeader>
              <ModalTitle>Create Custom Role</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {createError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Role Name (slug)"
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                      }))
                    }
                    required
                    placeholder="e.g. content_reviewer"
                    pattern="^[a-z][a-z0-9_]*$"
                    title="Lowercase letters, numbers, and underscores only"
                  />
                  <Input
                    label="Display Name"
                    value={createForm.display_name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, display_name: e.target.value }))}
                    required
                    placeholder="e.g. Content Reviewer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Description
                  </label>
                  <Textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Optional description of this role's purpose"
                    rows={2}
                  />
                </div>
                {manifest && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Permissions ({createPermissions.size} selected)
                    </label>
                    <div className="max-h-80 overflow-y-auto border border-border rounded-md p-2">
                      <PermissionPicker
                        manifest={manifest}
                        selectedKeys={createPermissions}
                        onChange={setCreatePermissions}
                      />
                    </div>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isCreating}>
                Create Role
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* ── Edit/View Role Modal ── */}
      <Modal open={!!editRole} onOpenChange={(open) => !open && setEditRole(null)}>
        <ModalContent size="lg">
          <form onSubmit={handleSave}>
            <ModalHeader>
              <ModalTitle>
                <div className="flex items-center gap-2">
                  {editRole?.is_system && <Lock className="w-4 h-4 text-muted-foreground" />}
                  {editRole?.is_system ? 'View Role' : 'Edit Role'}
                  {editRole?.is_system && (
                    <Badge variant="secondary" className="text-xs">System</Badge>
                  )}
                </div>
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                {editRole?.is_system && (
                  <Alert variant="info" className="mb-2">
                    <AlertDescription>
                      System roles cannot be modified. They provide the built-in permission hierarchy.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Role Name"
                    value={editRole?.name || ''}
                    disabled
                  />
                  <Input
                    label="Display Name"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))}
                    required
                    disabled={editRole?.is_system}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Description
                  </label>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    rows={2}
                    disabled={editRole?.is_system}
                  />
                </div>
                {editRole?.parent_role_name && (
                  <div className="text-sm text-muted-foreground">
                    Inherits from: <Badge variant="secondary">{editRole.parent_role_name}</Badge>
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>
                    <Users className="w-3.5 h-3.5 inline mr-1" />
                    {editRole?.user_count || 0} user{editRole?.user_count !== 1 ? 's' : ''} assigned
                  </span>
                  <span>{editPermissions.size} permission{editPermissions.size !== 1 ? 's' : ''}</span>
                </div>
                {manifest && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Permissions
                    </label>
                    <div className="max-h-80 overflow-y-auto border border-border rounded-md p-2">
                      <PermissionPicker
                        manifest={manifest}
                        selectedKeys={editPermissions}
                        onChange={setEditPermissions}
                        readOnly={editRole?.is_system}
                      />
                    </div>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" type="button" onClick={() => setEditRole(null)}>
                {editRole?.is_system ? 'Close' : 'Cancel'}
              </Button>
              {!editRole?.is_system && (
                <Button type="submit" isLoading={isSaving}>
                  Save Changes
                </Button>
              )}
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <Modal open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Delete Role</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Alert variant="destructive">
              <AlertDescription>
                <div>
                  <p className="font-medium">This action cannot be undone.</p>
                  <p className="mt-1">
                    Deleting the role <strong>{deleteTarget?.display_name}</strong> will
                    permanently remove it. Users currently assigned this role will lose
                    its permissions.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              isLoading={isDeleting}
            >
              Delete Role
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
